import React from 'react';
import PropTypes from 'prop-types';

import { getTranslation } from 'common/react/utils/translations';
import { useDispatch, useSelector } from 'react-redux';
import { navSelector } from 'store/modules/competition-nav/selectors';
import {
    changeCompetition,
    changeSeason
} from 'store/modules/competition-nav/thunks';
import { setNavItem } from 'store/modules/competition-nav/init';

/**
 * @param {object} props - Props.
 * @param {Array<string>} [props.filterKeys] - Keys to check for existing filters.
 * @param {string|number} [props.competitionId] - Competition to reset to,
 * normally the widget config value. Falls back to the nav's initial competition.
 * @param {string|number} [props.seasonId] - Season to reset to, normally the
 * widget config value. Falls back to the nav's initial season.
 * @returns {React.ReactElement} Component.
 */

export const ResetFilters = ({ filterKeys = [], competitionId, seasonId }) => {
    const nav = useSelector(navSelector);
    const dispatch = useDispatch();
    const initialCompetitionId =
        Number.parseInt(competitionId) || nav?.competition?.initialItemId || '';
    const initialSeasonId =
        Number.parseInt(seasonId) || nav?.season?.initialItemId || '';
    const isDefaultCompetition = nav?.competition?.id === initialCompetitionId;
    const isDefaultSeason = nav?.season?.id === initialSeasonId;
    const isDefaultHomeAway = nav?.homeAway === 'all';

    const resetHandler = () => {
        if (!isDefaultCompetition) {
            /**
             * Pass the season through rather than dispatching changeSeason
             * separately: changeCompetition resolves its own season once the
             * comp seasons load, so a parallel changeSeason gets overwritten.
             */
            dispatch(
                changeCompetition({
                    competition: {
                        id: initialCompetitionId
                    },
                    season: {
                        id: initialSeasonId
                    }
                })
            );
        } else if (!isDefaultSeason) {
            // Full season object so the current round can be resolved
            const season = nav?.seasons?.find(
                (item) => item.id === initialSeasonId
            ) || { id: initialSeasonId };

            dispatch(changeSeason({ season }));
        }
        if (!isDefaultHomeAway) {
            dispatch(
                setNavItem({
                    navItem: 'homeAway',
                    value: 'all'
                })
            );
        }
    };

    const hasAnyFilters = () => {
        const filters = nav?.filters || {};
        return (
            filterKeys.some(
                (key) => Array.isArray(filters[key]) && filters[key].length > 0
            ) ||
            !isDefaultCompetition ||
            !isDefaultSeason ||
            !isDefaultHomeAway
        );
    };

    return (
        <div className="stats-leaders-filters">
            <div className="stats-leaders-filters__item-wrapper stats-leaders-filters__item-wrapper--reset">
                {hasAnyFilters() ? (
                    <button
                        className="stats-leaders-filter-reset-button"
                        onClick={resetHandler}
                    >
                        {getTranslation(
                            'label.statsLeaders.filters.resetButtonText'
                        )}
                    </button>
                ) : null}
            </div>
        </div>
    );
};

ResetFilters.propTypes = {
    filterKeys: PropTypes.arrayOf(PropTypes.string),
    competitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    seasonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
